// teamHubService.ts - CORRECTED version
import request from "../../utils/request";
import {
  TeamHubPostInput,
  TeamHubPostView,
  TeamHubPostCommentInput,
  TeamHubPostCommentView,
  TeamHubFilterOptions,
} from "../../models/preformanceManagement";
import { Result } from "../../models/general";
import { apiErrorsOf } from "../../utils/requestHandling";

/**
 * CO-569: every catch here collapsed the rejection to `error instanceof Error ? error.message : <fallback>`.
 * The axios interceptor rejects with the PARSED `Result` body (a plain object, not an `Error`), so that
 * test was always false and the server's own refusal text never left this layer. Read it through
 * `apiErrorsOf`, which walks the wrappers and the non-enumerable `__apiErrors` stamp.
 */
function failureMessage(error: unknown, fallback: string): string {
  const descriptions = apiErrorsOf(error)
    .map((e) => e.description)
    .filter((d): d is string => typeof d === "string" && d.trim().length > 0);
  if (descriptions.length > 0) return descriptions.join("\n");
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export class TeamHubApiService {
  private baseUrl = "TeamHub"; // Added "api/" prefix to match controller route

  // Create a post - CORRECT (matches controller)
  async createPost(input: TeamHubPostInput): Promise<Result<TeamHubPostView>> {
    try {
      const response = await request.post<Result<TeamHubPostView>>(
        `${this.baseUrl}/CreatePost`, // Correct: matches [HttpPost("CreatePost")]
        input
      );
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: failureMessage(error, "Failed to create post"),
        errors: [],
        data: undefined,
      } as Result<TeamHubPostView>;
    }
  }

  // Create a comment - FIXED endpoint name
  async createComment(input: TeamHubPostCommentInput): Promise<Result<TeamHubPostCommentView>> {
    try {
      const response = await request.post<Result<TeamHubPostCommentView>>(
        `${this.baseUrl}/CreateComment`,
        input
      );
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: failureMessage(error, "Failed to create comment"),
        errors: [],
        data: undefined,
      } as Result<TeamHubPostCommentView>;
    }
  }


  // Get posts with filters - FIXED endpoint and parameter handling
  async getPosts(filters?: TeamHubFilterOptions): Promise<Result<TeamHubPostView[]>> {
    try {
      // Simplified parameter handling - controller expects simple query params
      const params = new URLSearchParams();
      if (filters) {
        // Only add parameters that match controller's expected query params
        if (filters.department) params.append('department', filters.department);
        if (filters.postType) params.append('postType', filters.postType);
        if (filters.search) params.append('search', filters.search);
        
        // Note: Controller doesn't handle array parameters for these fields
        // So we don't include tags, startDate, endDate, page, pageSize
      }

      // Changed from "posts" to "GetPosts" to match controller
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await request.get<Result<TeamHubPostView[]>>(
        `${this.baseUrl}/GetPosts${queryString}` // Fixed: matches [HttpGet("GetPosts")]
      );
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: failureMessage(error, "Failed to fetch posts"),
        errors: [],
        data: [],
      } as Result<TeamHubPostView[]>;
    }
  }

  // Get a specific post - FIXED endpoint
  async getPost(postId: number): Promise<Result<TeamHubPostView>> {
    try {
      // Changed from "posts/{id}" to "GetPost/{id}" to match controller
      const response = await request.get<Result<TeamHubPostView>>(
        `${this.baseUrl}/GetPost/${postId}` // Fixed: matches [HttpGet("GetPost/{id}")]
      );
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: failureMessage(error, "Failed to fetch post"),
        errors: [],
        data: undefined,
      } as Result<TeamHubPostView>;
    }
  }

  // Delete a post - FIXED endpoint
  async deletePost(postId: number): Promise<Result<boolean>> {
    try {
      // Changed from "posts/{id}" to "DeletePost/{id}" to match controller
      const response = await request.delete<Result<boolean>>(
        `${this.baseUrl}/DeletePost/${postId}` // Fixed: matches [HttpDelete("DeletePost/{id}")]
      );
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: failureMessage(error, "Failed to delete post"),
        errors: [],
        data: false,
      } as Result<boolean>;
    }
  }

  // Delete a comment - FIXED endpoint
  async deleteComment(commentId: number): Promise<Result<boolean>> {
    try {
      // Changed from "comments/{id}" to "DeleteComment/{id}" to match controller
      const response = await request.delete<Result<boolean>>(
        `${this.baseUrl}/DeleteComment/${commentId}` // Fixed: matches [HttpDelete("DeleteComment/{id}")]
      );
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: failureMessage(error, "Failed to delete comment"),
        errors: [],
        data: false,
      } as Result<boolean>;
    }
  }

  // Get comments for a specific post
  async getComments(postId: number): Promise<Result<TeamHubPostCommentView[]>> {
    try {
      const response = await request.get<Result<TeamHubPostCommentView[]>>(
        `${this.baseUrl}/GetComments/${postId}`
      );
      return response.data;
    } catch (error) {
      return {
        success: false,
        message: failureMessage(error, "Failed to fetch comments"),
        errors: [],
        data: [],
      } as Result<TeamHubPostCommentView[]>;
    }
  }
}

export const teamHubApi = new TeamHubApiService();